// SelectCell — shared controlled dropdown primitive for inline grid edits.
// Domain-neutral (Component_Contract Tier 1). Theme tokens only.
//
// Props:
//   value        — current value (controlled)
//   onChange     — (nextValue: string) => void
//   options      — [{ value, label }] OR [string]  (string form: value === label, '' shows '—')
//   placeholder? — reserved (kept for API symmetry with TextCell); not rendered as an option
//   title?       — tooltip
//   style?       — merged over the cell style, for a control sitting outside a grid
//                  (the chat's model picker is smaller than a grid cell)
//
// Data preservation: if the current `value` is not present in `options`, SelectCell
// injects it as an extra option so an off-list existing value is never dropped/blanked
// (honours the config-unit "preserve off-list value" rule). Stops click propagation.

import { CELL_INPUT_STYLE } from './cellStyles.js';

// Normalize an option (string | {value,label}) → { value, label }.
function norm(opt) {
  if (typeof opt === 'string') return { value: opt, label: opt || '—' };
  return { value: opt.value, label: opt.label };
}

export default function SelectCell({ value, onChange, options = [], title, style }) {
  const norm_ = options.map(norm);
  const cur = value == null ? '' : String(value);
  // Preserve an existing off-list value: if `cur` isn't among the options, add it.
  const hasCur = norm_.some(o => String(o.value) === cur);
  const finalOpts = hasCur ? norm_ : [...norm_, { value: cur, label: cur || '—' }];

  return (
    <select
      value={cur}
      title={title}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      style={style ? { ...CELL_INPUT_STYLE, ...style } : CELL_INPUT_STYLE}
    >
      {finalOpts.map(o => (
        <option key={String(o.value)} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
